import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { assertOk } from '../../../components/admin/assertOk';
import type { GroupSlackChannelsApi } from './GroupSlackChannelsApi';
import type {
  GroupSlackChannel,
  GroupSlackChannelInput,
  SlackChannelQuery,
  SlackChannelSearch,
} from './types';

export class GroupSlackChannelsClient implements GroupSlackChannelsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async listMappings(): Promise<GroupSlackChannel[]> {
    const body = await this.request<{ mappings?: GroupSlackChannel[] }>('');
    return body.mappings ?? [];
  }

  async setMapping(mapping: GroupSlackChannelInput): Promise<void> {
    await this.send('', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mapping),
    });
  }

  async deleteMapping(groupRef: string): Promise<void> {
    await this.send(`/${encodeURIComponent(groupRef)}`, { method: 'DELETE' });
  }

  async listChannels(
    query: SlackChannelQuery = {},
  ): Promise<SlackChannelSearch> {
    const params = new URLSearchParams();
    if (query.q) {
      params.set('q', query.q);
    }
    if (query.limit !== undefined) {
      params.set('limit', String(query.limit));
    }
    const search = params.toString();
    const body = await this.request<Partial<SlackChannelSearch>>(
      `/channels${search ? `?${search}` : ''}`,
    );
    return {
      channels: body.channels ?? [],
      truncated: !!body.truncated,
      incomplete: !!body.incomplete,
    };
  }

  private async url(path: string): Promise<string> {
    const base = await this.discoveryApi.getBaseUrl('catalog');
    return `${base}/group-slack-channels${path}`;
  }

  private async send(path: string, init?: RequestInit): Promise<void> {
    const response = await this.fetchApi.fetch(await this.url(path), init);
    await assertOk(response);
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await this.fetchApi.fetch(await this.url(path), init);
    await assertOk(response);
    return (await response.json()) as T;
  }
}
